// Страница «Для репетиторов и кружков» (/tutors/join) — тексты на трёх языках.
//
// Тексты страницы — из design/reference/TutorJoin.dc.html (RU), условия,
// экран после отправки и письма — из design/content/partners.md, названия
// режимов адреса, форматов, районов и предметов — из
// design/content/map-taxonomy.json (здесь их нет).
// Отступление от мокапа одно: «квартира, этаж и телефон видны после записи»
// → квартиру и этаж мы не спрашиваем вовсе (решение владельца 06.10.2026).
// AZ — на вычитку.
import { TUTORS_JOIN_HREF, type SiteLang } from "@/components/site/SiteChrome";

/** Адреса страницы — те же, что у ссылки в подвале. */
export const TUTORS_JOIN_PATH: Record<SiteLang, string> = TUTORS_JOIN_HREF;

/** Экран «Готово!» после отправки анкеты (24.10.2026): свой адрес, в GA — конверсия. */
export const TUTORS_JOIN_SENT_PATH: Record<SiteLang, string> = {
  ru: "/ru/tutors/join/sent",
  az: "/az/tutors/join/sent",
  en: "/en/tutors/join/sent",
};

/** Условия для партнёров — блок на этой же странице, под формой. */
export const PARTNER_TERMS_ID = "partner-terms";

/** Якорь формы: кнопка первого экрана ведёт сюда (#form), работает и без JS. */
export const PARTNER_FORM_ID = "form";

type ModeText = { building: string; travel: string; online: string };

type Copy = {
  meta: { title: string; description: string };
  h1: string;
  h1Accent: string;
  lead: string;
  /** Кнопка первого экрана → форма (#form), ТЗ «два пути». */
  cta: string;
  previewTag: string;
  previewName: string;
  previewSubject: string;
  whyTitle: string;
  why: { title: string; text: string }[];
  whoTitle: string;
  who: { title: string; text: string; points: string[] }[];
  privacyTitle: string;
  privacyLead: string;
  /** Пояснения к трём режимам в тёмном блоке — из мокапа. */
  modeText: Pick<ModeText, "building" | "travel">;
  formTitle: string;
  kindGroup: string;
  kindTutor: string;
  kindClub: string;
  nameTutor: string;
  nameClub: string;
  namePhTutor: string;
  namePhClub: string;
  subjectsTutor: string;
  subjectsClub: string;
  chosen: string;
  searchTutor: string;
  searchClub: string;
  searchLabel: string;
  categories: string;
  noHits: string;
  priceTutor: string;
  priceClub: string;
  /** Формат цены (PR B): урок / месяц / пакет. club — «məşğələ» вместо «dərs». */
  priceUnit: string;
  priceUnits: (club: boolean) => { lesson: string; month: string; package: string };
  priceUnitNote: string;
  lessonsPerMonth: (club: boolean) => string;
  lessonsInPackage: (club: boolean) => string;
  lessonMinutes: (club: boolean) => string;
  other: string;
  lessonPriceHint: string;
  format: string;
  trialFree: string;
  /** «Язык уроков» (20261118120000): заголовок и подсказка под ним. */
  teachLangs: string;
  teachLangsHint: string;
  grades: string;
  age: string;
  ageFrom: string;
  ageTo: string;
  years: string;
  pinTitleTutor: string;
  pinTitleClub: string;
  district: string;
  travelDistricts: string;
  /** Галочка под «Моё здание»: районы выезда сверх точки на карте. */
  alsoTravel: string;
  /** Строка превью карточки: «Также приезжает к ученику: …». */
  alsoTravelsTo: string;
  address: string;
  addressPh: string;
  mapHint: string;
  mapLoading: string;
  mapNoKey: string;
  /** Поиск Google над картой. */
  searchPh: string;
  /** После первого нажатия на отдалённую карту: приблизили, пина ещё нет. */
  mapZoomed: string;
  /** Строка под картой с поставленным пином: «📍 Yasamal · m. İnşaatçılar — düzdür?» и «Dəyiş». */
  metroAbbr: string;
  pinConfirm: string;
  /** Ориентир (необязательный) и «Digər» (20261031120000). */
  landmark: string;
  landmarkPh: string;
  optional: string;
  subjectOther: string;
  subjectOtherPh: string;
  /** Строка под пином в Баку: район и метро по координатам. */
  pinPlaceLine: (district: string, metro: string | null) => string;
  districtFix: string;
  pinChange: string;
  /** Район анкеты и район ближайшего метро не сходятся — мягко, отправку не блокирует. */
  pinMetroWarn: (metro: string) => string;
  /** Ручной выбор метро («Dəyiş», 26.10.2026). */
  metroLabel: string;
  metroNone: string;
  /** Город или район страны (вне Баку районов и метро нет). */
  city: string;
  cityPick: string;
  /** Поиск города (26.10.2026): подсказка в поле и «ничего не нашлось». */
  cityFind: string;
  cityNoHits: string;
  /** Экран выбора точки на весь экран (как Wolt/Bolt, 26.10.2026). */
  mapOpen: string;
  mapReopen: string;
  mapChoose: string;
  mapMe: string;
  mapMeFail: string;
  mapBack: string;
  mapDragHint: string;
  /** Выезд вне Баку — по городу целиком. */
  travelWholeCity: (city: string) => string;
  phone: string;
  instagram: string;
  /** Под полем Instagram, когда ник получился: «instagram.com/<ник> — это ваш профиль?» (26.10.2026). */
  igCheck: string;
  /** Подпись под почтой: зачем она обязательна. */
  emailNote: string;
  email: string;
  about: string;
  consent: [string, string, string];
  send: string;
  sending: string;
  sent: { title: string; text: string; button: string };
  errGeneric: string;
  /** Пустой выбор района: по умолчанию район не стоит. */
  districtPick: string;
  /** Над кнопкой, когда что-то не заполнено. */
  errCheck: string;
  /** Под конкретным полем. */
  errs: {
    nameTutor: string;
    nameClub: string;
    subjectsTutor: string;
    subjectsClub: string;
    price: string;
    age: string;
    pin: string;
    address: string;
    district: string;
    travel: string;
    phone: string;
    email: string;
    emailEmpty: string;
    instagram: string;
    mode: string;
    city: string;
    pinOutside: string;
    consent: string;
    priceUnit: string;
    lessons: string;
  };
  seenAs: string;
  /** Предпросмотр перед отправкой. */
  pvLead: string;
  pvFix: string;
  pvSend: string;
  pvBack: string;
  pvNone: string;
  pvName: string;
  pvSubjects: string;
  pvPrice: string;
  pvPlace: string;
  pvDetails: string;
  pvContacts: string;
  chooseSubjects: string;
  chooseDirections: string;
  perLesson: string;
  perMonth: string;
  pinOnMap: string;
  pinText: ModeText & { club: string };
  nearMetro: string;
  termsTitle: string;
  terms: [string, string][];
  faqTitle: string;
  faq: { q: string; a: string }[];
};

export const PARTNER_COPY: Record<SiteLang, Copy> = {
  ru: {
    meta: {
      title: "Станьте партнёром Yolutap — репетиторы и кружки",
      description: "Бесплатная регистрация для репетиторов и кружков Азербайджана: место на карте рядом с домом и ученики, которые знают, зачем пришли.",
    },
    h1: "Ученики рядом с домом,",
    h1Accent: "которые знают, зачем пришли.",
    lead: "Yolutap помогает семьям по всему Азербайджану понять, что подтянуть и что развивать у ребёнка. Дальше мы показываем им репетиторов и кружки рядом с домом. Регистрация бесплатная.",
    cta: "Заполнить анкету",
    previewTag: "Так вас увидят родители",
    previewName: "Ваше имя",
    previewSubject: "Математика",
    whyTitle: "Родитель приходит не «искать репетитора вообще»",
    why: [
      { title: "С конкретной задачей", text: "Отчёт Yolutap уже показал, какой предмет подтянуть и почему. Родитель видит вас в своей задаче: «Математика · 9 класс · рядом с домом»." },
      { title: "Из вашего района", text: "На карте вас ищут рядом с домом — в вашем городе и районе. Ученикам не нужно ехать далеко, а вам — тратить время на дорогу." },
    ],
    whoTitle: "Для кого",
    who: [
      { title: "Репетиторы", text: "Школьные предметы, экзамены (DİM, IELTS, SAT), языки, IT, музыка. Занимаетесь у себя, ездите к ученикам или онлайн — выбираете сами.", points: ["Пин на карте с вашей ценой за урок"] },
      { title: "Кружки и секции", text: "Робототехника, программирование, искусство, музыка, дебаты, шахматы, спорт.", points: ["Точный пин на здании и цена в месяц"] },
    ],
    privacyTitle: "Что видно на карте — решаете вы",
    privacyLead: "Телефон не показывается на карте — родитель видит его, когда нажмёт «Позвонить». Квартиру и этаж мы не спрашиваем.",
    modeText: {
      building: "Пин на доме. Удобно для многоэтажек: видно реальную дорогу, но не квартиру.",
      travel: "Вместо пина — зона выезда по районам, которые вы выбрали.",
    },
    formTitle: "Регистрация — бесплатно",
    kindGroup: "Кто вы",
    kindTutor: "Я репетитор",
    kindClub: "Кружок, курс или центр",
    nameTutor: "Имя и фамилия",
    nameClub: "Название кружка",
    namePhTutor: "Aysel Məmmədova",
    namePhClub: "RoboLab",
    subjectsTutor: "Предметы",
    subjectsClub: "Направления",
    chosen: "выбрано",
    searchTutor: "Найти предмет: riyaziyyat, IELTS, gitara…",
    searchClub: "Найти направление: robototexnika, rəsm…",
    searchLabel: "Поиск предмета",
    categories: "Категории",
    noHits: "Не нашли? Напишите предмет в комментарии к заявке — добавим.",
    priceTutor: "Цена, ₼",
    priceClub: "Цена, ₼",
    priceUnit: "За что эта цена",
    priceUnits: (club) => ({ lesson: club ? "За занятие" : "За урок", month: "В месяц", package: "Пакет" }),
    priceUnitNote: "Выберите формат, чтобы родители могли сравнить цены",
    lessonsPerMonth: (club) => (club ? "Сколько занятий в месяц" : "Сколько уроков в месяц"),
    lessonsInPackage: (club) => (club ? "Сколько занятий в пакете" : "Сколько уроков в пакете"),
    lessonMinutes: (club) => (club ? "Сколько минут длится занятие" : "Сколько минут длится урок"),
    other: "Другое",
    lessonPriceHint: "Это цена одного урока? Если это плата за месяц, выберите «В месяц».",
    format: "Формат",
    trialFree: "Первое занятие бесплатно",
    teachLangs: "Язык уроков",
    teachLangsHint: "На каких языках вы ведёте уроки — можно несколько. По нему подбираем вас родителям.",
    grades: "Классы",
    age: "Возраст",
    ageFrom: "от",
    ageTo: "до",
    years: "лет",
    pinTitleTutor: "Где вы на карте",
    pinTitleClub: "Адрес на карте",
    district: "Район",
    travelDistricts: "Районы выезда",
    alsoTravel: "Также могу приезжать к ученику",
    alsoTravelsTo: "Также приезжает к ученику",
    address: "Адрес",
    addressPh: "Адрес: улица и номер дома",
    mapHint: "Нажмите на своё здание — пин встанет на него",
    mapLoading: "Загружаем карту…",
    mapNoKey: "Карта сейчас недоступна — укажите адрес текстом, мы поставим пин при проверке.",
    searchPh: "Адрес или метро",
    mapZoomed: "Приблизили — сдвиньте карту, чтобы пин встал на ваш дом, и выберите место",
    metroAbbr: "м.",
    pinConfirm: "верно?",
    landmark: "Ориентир",
    landmarkPh: "Например: напротив школы №6, вход со двора",
    optional: "· необязательно",
    subjectOther: "Другое",
    subjectOtherPh: "Какой предмет? Например: шахматы для взрослых",
    pinPlaceLine: (d, m) => (m ? `Район: ${d} · Метро: ${m}` : `Район: ${d}`),
    districtFix: "Изменить",
    pinChange: "Убрать пин",
    metroLabel: "Метро",
    metroNone: "Метро рядом нет",
    pinMetroWarn: (m) => `Пин у м. ${m} — верно?`,
    city: "Город / район",
    cityPick: "Выберите",
    cityFind: "Начните вводить: Хырдалан, Сумгаит…",
    cityNoHits: "Не нашли — попробуйте иначе",
    mapOpen: "Выбрать на карте",
    mapReopen: "Изменить на карте",
    mapChoose: "Выбрать это место",
    mapMe: "Моё место",
    mapMeFail: "Не удалось определить место — передвиньте карту сами",
    mapBack: "Назад",
    mapDragHint: "Двигайте карту одним пальцем — пин стоит в центре",
    travelWholeCity: (city) => `По всему городу/району: ${city}`,
    phone: "Телефон (WhatsApp) *",
    instagram: "Ваш Instagram (по желанию)",
    igCheck: "это ваш профиль?",
    emailNote: "Сюда придёт ссылка для редактирования профиля и результат проверки",
    email: "Почта *",
    about: "Комментарий к заявке",
    consent: ["Я соглашаюсь с ", "Условиями для партнёров", ""],
    send: "Отправить заявку",
    sending: "Отправляем…",
    sent: { title: "Анкета отправлена", text: "Мы проверим анкету и напишем на [email] перед публикацией.", button: "На главную" },
    errGeneric: "Не удалось отправить заявку. Попробуйте ещё раз или напишите на support@yolutap.com.",
    districtPick: "Выберите район",
    errCheck: "Заявка не отправлена: проверьте поля, отмеченные красным.",
    errs: {
      nameTutor: "Укажите имя — не короче двух букв.",
      nameClub: "Укажите название.",
      subjectsTutor: "Выберите хотя бы один предмет.",
      subjectsClub: "Выберите хотя бы одно направление.",
      price: "Цена — только число.",
      age: "Возраст «от» не может быть больше, чем «до».",
      pin: "Поставьте точку на своём здании.",
      address: "Карта не загрузилась — укажите адрес текстом.",
      district: "Выберите район.",
      travel: "Выберите хотя бы один район выезда.",
      phone: "Укажите телефон: цифры, +, пробелы, скобки или дефис.",
      email: "Проверьте почту — в адресе ошибка.",
      emailEmpty: "Укажите почту — на неё придёт результат проверки.",
      instagram: "Только ник, например gulay_muellim",
      mode: "Выберите: моё здание, езжу к ученикам или только онлайн",
      city: "Выберите город или район.",
      pinOutside: "Пин за пределами Азербайджана — найдите свой дом на карте.",
      consent: "Подтвердите согласие с условиями.",
      priceUnit: "Выберите, за что эта цена: урок, месяц или пакет.",
      lessons: "Укажите, сколько уроков в пакете.",
    },
    seenAs: "Так вас увидят родители",
    pvLead: "Проверьте карточку перед отправкой. Ошиблись — нажмите «Исправить» рядом с блоком.",
    pvFix: "Исправить",
    pvSend: "Всё верно — отправить",
    pvBack: "Назад к анкете",
    pvNone: "не указана",
    pvName: "Имя и фото",
    pvSubjects: "Предметы",
    pvPrice: "Цена",
    pvPlace: "Где занятия",
    pvDetails: "Формат и классы",
    pvContacts: "Контакты",
    chooseSubjects: "Выберите предметы",
    chooseDirections: "Выберите направления",
    perLesson: "за урок",
    perMonth: "в месяц",
    pinOnMap: "Пин на карте",
    pinText: { club: "точно на здании организации", building: "на вашем здании, без квартиры и этажа", travel: "зона выезда вместо пина", online: "без пина, в списке «Онлайн»" },
    nearMetro: "у метро",
    termsTitle: "Условия — коротко",
    terms: [
      ["Бесплатно.", "Размещение на карте ничего не стоит: ни абонентской платы, ни комиссии с учеников."],
      ["Ваш адрес — под вашим контролем.", "Вы решаете, что видят родители: точное здание или зону, куда вы готовы выезжать."],
      ["Только правда.", "Указывайте реальные предметы, цены и опыт."],
      ["Мы проверяем анкету", "перед публикацией и можем задать уточняющий вопрос."],
      ["Карта не влияет на отчёты.", "Рекомендации в карьерных отчётах Yolutap не зависят от того, кто размещён на карте."],
      ["Изменить или удалить анкету", "можно в любой момент: напишите на support@yolutap.com."],
      ["Если условия изменятся,", "мы заранее напишем каждому партнёру. Оставаться или нет, решаете вы."],
    ],
    faqTitle: "Вопросы",
    faq: [
      { q: "Сколько это стоит?", a: "Регистрация, анкета и место на карте — бесплатно, без комиссии с учеников. Если условия когда-нибудь изменятся, мы заранее напишем каждому партнёру." },
      { q: "Когда я появлюсь на карте?", a: "Сейчас мы собираем первых партнёров. Мы проверим анкету и напишем вам перед публикацией." },
      { q: "Влияет ли карта на отчёт ребёнка?", a: "Нет. Рекомендации отчёта никогда не зависят от того, кто размещён на карте. Репетиторы и кружки показываются отдельным блоком с подписью «Партнёры Yolutap рядом с вами»." },
      { q: "Я преподаю онлайн. Мне подходит?", a: "Да. Онлайн-репетиторы показываются отдельным списком без пина на карте." },
    ],
  },
  az: {
    meta: {
      title: "Yolutap tərəfdaşı olun — repetitorlar və dərnəklər",
      description: "Azərbaycanın repetitorları və dərnəkləri üçün pulsuz qeydiyyat: evə yaxın xəritədə yer və nə üçün gəldiyini bilən şagirdlər.",
    },
    h1: "Evə yaxın şagirdlər —",
    h1Accent: "nə üçün gəldiklərini bilənlər.",
    lead: "Yolutap bütün Azərbaycanda ailələrə uşağın nəyi gücləndirməli və nəyi inkişaf etdirməli olduğunu anlamağa kömək edir. Sonra onlara evə yaxın repetitorları və dərnəkləri göstəririk. Qeydiyyat pulsuzdur.",
    cta: "Anketi doldur",
    previewTag: "Valideynlər sizi belə görəcək",
    previewName: "Adınız",
    previewSubject: "Riyaziyyat",
    whyTitle: "Valideyn «ümumiyyətlə repetitor axtarmağa» gəlmir",
    why: [
      { title: "Konkret məqsədlə", text: "Yolutap hesabatı artıq hansı fənni gücləndirmək lazım olduğunu və səbəbini göstərib. Valideyn sizi öz məqsədində görür: «Riyaziyyat · 9-cu sinif · evə yaxın»." },
      { title: "Sizin rayondan", text: "Xəritədə sizi evə yaxın axtarırlar — öz şəhərinizdə və rayonunuzda. Şagirdlər uzağa getməli olmur, siz də yola vaxt itirmirsiniz." },
    ],
    whoTitle: "Kimin üçün",
    who: [
      { title: "Repetitorlar", text: "Məktəb fənləri, imtahanlar (DİM, IELTS, SAT), dillər, İT, musiqi. Öz yerinizdə, şagirdin evində və ya onlayn — özünüz seçirsiniz.", points: ["Dərs qiyməti ilə xəritədə pin"] },
      { title: "Dərnəklər və bölmələr", text: "Robototexnika, proqramlaşdırma, incəsənət, musiqi, debat, şahmat, idman.", points: ["Binada dəqiq pin və aylıq qiymət"] },
    ],
    privacyTitle: "Xəritədə nəyin görünəcəyinə siz qərar verirsiniz",
    privacyLead: "Telefon xəritədə göstərilmir — valideyn onu «Zəng et» düyməsini basanda görür. Mənzil və mərtəbəni soruşmuruq.",
    modeText: {
      building: "Pin evin üzərində. Çoxmərtəbəli binalar üçün rahatdır: real yol görünür, mənzil yox.",
      travel: "Pin əvəzinə — seçdiyiniz rayonlar üzrə gediş zonası.",
    },
    formTitle: "Qeydiyyat — pulsuz",
    kindGroup: "Siz kimsiniz",
    kindTutor: "Mən repetitoram",
    kindClub: "Dərnək, kurs və ya mərkəz",
    nameTutor: "Ad və soyad",
    nameClub: "Dərnəyin adı",
    namePhTutor: "Aysel Məmmədova",
    namePhClub: "RoboLab",
    subjectsTutor: "Fənlər",
    subjectsClub: "İstiqamətlər",
    chosen: "seçilib",
    searchTutor: "Fənn tap: riyaziyyat, IELTS, gitara…",
    searchClub: "İstiqamət tap: robototexnika, rəsm…",
    searchLabel: "Fənn axtarışı",
    categories: "Kateqoriyalar",
    noHits: "Tapmadınız? Fənni müraciətə şərhdə yazın — əlavə edəcəyik.",
    priceTutor: "Qiymət, ₼",
    priceClub: "Qiymət, ₼",
    priceUnit: "Qiymət nə üçündür",
    priceUnits: (club) => ({ lesson: club ? "Bir məşğələ" : "Bir dərs", month: "Aylıq", package: "Paket" }),
    priceUnitNote: "Valideynlər qiymətləri müqayisə edə bilsin deyə formatı seçin",
    lessonsPerMonth: (club) => (club ? "Ayda neçə məşğələ" : "Ayda neçə dərs"),
    lessonsInPackage: (club) => (club ? "Paketdə neçə məşğələ" : "Neçə dərs"),
    lessonMinutes: (club) => (club ? "Məşğələ neçə dəqiqədir" : "Dərs neçə dəqiqədir"),
    other: "Başqa",
    lessonPriceHint: "Bu, bir dərsin qiymətidir? Aylıq ödənişdirsə, “Aylıq” seçin.",
    format: "Format",
    trialFree: "İlk dərs pulsuzdur",
    teachLangs: "Dərs dili",
    teachLangsHint: "Dərsləri hansı dillərdə keçirsiniz — bir neçəsini seçə bilərsiniz. Valideynlərə sizi buna görə seçirik.",
    grades: "Siniflər",
    age: "Yaş",
    ageFrom: "-dan",
    ageTo: "-dək",
    years: "yaş",
    pinTitleTutor: "Xəritədə haradasınız",
    pinTitleClub: "Xəritədə ünvan",
    district: "Rayon",
    travelDistricts: "Gediş rayonları",
    alsoTravel: "Şagirdin yanına da gedə bilərəm",
    alsoTravelsTo: "Şagirdin yanına da gedir",
    address: "Ünvan",
    addressPh: "Ünvan: küçə və ev nömrəsi",
    mapHint: "Öz binanıza toxunun — pin onun üzərinə düşəcək",
    mapLoading: "Xəritə yüklənir…",
    mapNoKey: "Xəritə hazırda əlçatan deyil — ünvanı mətnlə yazın, pini yoxlama zamanı qoyacağıq.",
    searchPh: "Ünvan və ya metro",
    mapZoomed: "Yaxınlaşdırdıq — xəritəni sürüşdürün ki, pin binanızın üstündə olsun, sonra yeri seçin",
    metroAbbr: "m.",
    pinConfirm: "düzdür?",
    landmark: "Oriyentir",
    landmarkPh: "Məsələn: 6 nömrəli məktəbin qarşısı, giriş həyətdən",
    optional: "· istəyə görə",
    subjectOther: "Digər",
    subjectOtherPh: "Hansı fənn? Məsələn: böyüklər üçün şahmat",
    pinPlaceLine: (d, m) => (m ? `Rayon: ${d} · Metro: ${m}` : `Rayon: ${d}`),
    districtFix: "Dəyiş",
    pinChange: "Pini sil",
    metroLabel: "Metro",
    metroNone: "Yaxında metro yoxdur",
    pinMetroWarn: (m) => `Pin ${m} yanındadır — düzdürmü?`,
    city: "Şəhər / rayon",
    cityPick: "Seçin",
    cityFind: "Yazmağa başlayın: Xırdalan, Sumqayıt…",
    cityNoHits: "Tapılmadı — başqa cür yazın",
    mapOpen: "Xəritədə seç",
    mapReopen: "Xəritədə dəyiş",
    mapChoose: "Bu yeri seç",
    mapMe: "Mənim yerim",
    mapMeFail: "Yerinizi tapa bilmədik — xəritəni özünüz sürüşdürün",
    mapBack: "Geri",
    mapDragHint: "Xəritəni bir barmaqla sürüşdürün — pin mərkəzdə qalır",
    travelWholeCity: (city) => `${city} üzrə istənilən ünvana gedirəm`,
    phone: "Telefon (WhatsApp) *",
    instagram: "Instagram profiliniz (istəyə görə)",
    igCheck: "bu sizin profilinizdir?",
    emailNote: "Profili redaktə linki və yoxlama nəticəsi bu ünvana gələcək",
    email: "E-poçt *",
    about: "Müraciətə şərh",
    consent: ["", "Tərəfdaşlar üçün şərtlərlə", " razıyam"],
    send: "Müraciəti göndər",
    sending: "Göndərilir…",
    sent: { title: "Anket göndərildi", text: "Anketi yoxlayıb dərc etməzdən əvvəl [email] ünvanına yazacağıq.", button: "Ana səhifəyə" },
    errGeneric: "Müraciəti göndərmək alınmadı. Yenidən cəhd edin və ya support@yolutap.com ünvanına yazın.",
    districtPick: "Rayonu seçin",
    errCheck: "Müraciət göndərilmədi: qırmızı ilə qeyd olunan sahələri yoxlayın.",
    errs: {
      nameTutor: "Adınızı yazın — ən azı iki hərf.",
      nameClub: "Adı yazın.",
      subjectsTutor: "Ən azı bir fənn seçin.",
      subjectsClub: "Ən azı bir istiqamət seçin.",
      price: "Qiymət yalnız rəqəm olmalıdır.",
      age: "«-dan» yaşı «-dək» yaşından böyük ola bilməz.",
      pin: "Binanızın üzərinə nöqtə qoyun.",
      address: "Xəritə yüklənmədi — ünvanı mətnlə yazın.",
      district: "Rayonu seçin.",
      travel: "Ən azı bir gediş rayonu seçin.",
      phone: "Telefonu yazın: rəqəmlər, +, boşluq, mötərizə və ya tire.",
      email: "E-poçtu yoxlayın — ünvanda səhv var.",
      emailEmpty: "E-poçtu yazın — yoxlama nəticəsi ora gələcək.",
      instagram: "Yalnız ad: məsələn, gulay_muellim",
      mode: "Seçin: binam, şagirdin yanına gedirəm və ya yalnız onlayn",
      city: "Şəhəri və ya rayonu seçin.",
      pinOutside: "Pin Azərbaycandan kənardadır — binanızı xəritədə tapın.",
      consent: "Şərtlərlə razılığınızı təsdiqləyin.",
      priceUnit: "Qiymətin formatını seçin: bir dərs, aylıq və ya paket.",
      lessons: "Paketdə neçə dərs olduğunu yazın.",
    },
    seenAs: "Valideynlər sizi belə görəcək",
    pvLead: "Göndərməzdən əvvəl kartı yoxlayın. Səhv varsa, blokun yanındakı «Düzəlt» düyməsini basın.",
    pvFix: "Düzəlt",
    pvSend: "Hər şey düzdür — göndər",
    pvBack: "Ankete qayıt",
    pvNone: "göstərilməyib",
    pvName: "Ad və şəkil",
    pvSubjects: "Fənlər",
    pvPrice: "Qiymət",
    pvPlace: "Dərslər harada",
    pvDetails: "Format və siniflər",
    pvContacts: "Əlaqə",
    chooseSubjects: "Fənləri seçin",
    chooseDirections: "İstiqamətləri seçin",
    perLesson: "dərs üçün",
    perMonth: "aylıq",
    pinOnMap: "Xəritədə pin",
    pinText: { club: "təşkilatın binasında dəqiq", building: "binanızda, mənzil və mərtəbəsiz", travel: "pin əvəzinə gediş zonası", online: "pinsiz, «Onlayn» siyahısında" },
    nearMetro: "metro",
    termsTitle: "Şərtlər — qısaca",
    terms: [
      ["Pulsuzdur.", "Xəritədə yerləşmək heç nəyə başa gəlmir: nə abunə haqqı, nə də şagirdlərdən komissiya."],
      ["Ünvanınız sizin nəzarətinizdədir.", "Valideynlərin nə görəcəyini siz seçirsiniz: dəqiq bina və ya getməyə hazır olduğunuz ərazi."],
      ["Yalnız doğru məlumat.", "Real fənləri, qiymətləri və təcrübənizi yazın."],
      ["Anketi dərc etməzdən əvvəl yoxlayırıq", "və lazım olsa, sual verə bilərik."],
      ["Xəritə hesabatlara təsir etmir.", "Yolutap karyera hesabatlarındakı tövsiyələr xəritədə kimin olmasından asılı deyil."],
      ["Anketi istənilən vaxt dəyişmək və ya silmək", "üçün support@yolutap.com ünvanına yazın."],
      ["Şərtlər dəyişsə,", "hər tərəfdaşa əvvəlcədən yazacağıq. Qalıb-qalmamağı siz həll edirsiniz."],
    ],
    faqTitle: "Suallar",
    faq: [
      { q: "Bu neçəyədir?", a: "Qeydiyyat, anket və xəritədə yer — pulsuzdur, şagirdlərdən komissiya yoxdur. Şərtlər nə vaxtsa dəyişsə, hər tərəfdaşa əvvəlcədən yazacağıq." },
      { q: "Xəritədə nə vaxt görünəcəyəm?", a: "Hazırda ilk tərəfdaşları toplayırıq. Anketi yoxlayıb dərc etməzdən əvvəl sizə yazacağıq." },
      { q: "Xəritə uşağın hesabatına təsir edirmi?", a: "Xeyr. Hesabatın tövsiyələri heç vaxt xəritədə kimin olmasından asılı deyil. Repetitorlar və dərnəklər «Yaxınlığınızdakı Yolutap tərəfdaşları» başlığı ilə ayrıca blokda göstərilir." },
      { q: "Onlayn dərs deyirəm. Mənə uyğundurmu?", a: "Bəli. Onlayn repetitorlar xəritədə pinsiz, ayrıca siyahıda göstərilir." },
    ],
  },
  en: {
    meta: {
      title: "Become a Yolutap partner — tutors and clubs",
      description: "Free sign-up for tutors and clubs across Azerbaijan: a place on the map near home and students who know why they came.",
    },
    h1: "Students near home",
    h1Accent: "who know why they came.",
    lead: "Yolutap helps families across Azerbaijan understand what their child should catch up on and what to develop. Then we show them tutors and clubs near home. Sign-up is free.",
    cta: "Fill in the profile",
    previewTag: "How parents will see you",
    previewName: "Your name",
    previewSubject: "Maths",
    whyTitle: "Parents don't come to “find a tutor in general”",
    why: [
      { title: "With a specific goal", text: "The Yolutap report has already shown which subject to catch up on and why. Parents see you in their goal: “Maths · grade 9 · near home”." },
      { title: "From your district", text: "Parents search the map near home — in your town and district. Students don't have to travel far, and you don't waste time on the road." },
    ],
    whoTitle: "Who it's for",
    who: [
      { title: "Tutors", text: "School subjects, exams (DİM, IELTS, SAT), languages, IT, music. At your place, at the student's home or online — your choice.", points: ["A pin on the map with your price per lesson"] },
      { title: "Clubs and sections", text: "Robotics, programming, art, music, debate, chess, sport.", points: ["An exact pin on the building and a monthly price"] },
    ],
    privacyTitle: "You decide what the map shows",
    privacyLead: "Your phone number isn't shown on the map — a parent sees it when they tap “Call”. We don't ask for your flat or floor.",
    modeText: {
      building: "A pin on the building. Handy for blocks of flats: the real route is visible, the flat is not.",
      travel: "Instead of a pin — a travel zone across the districts you choose.",
    },
    formTitle: "Sign-up — free",
    kindGroup: "Who you are",
    kindTutor: "I'm a tutor",
    kindClub: "Club, course or center",
    nameTutor: "First and last name",
    nameClub: "Club name",
    namePhTutor: "Aysel Mammadova",
    namePhClub: "RoboLab",
    subjectsTutor: "Subjects",
    subjectsClub: "Activities",
    chosen: "selected",
    searchTutor: "Find a subject: maths, IELTS, guitar…",
    searchClub: "Find an activity: robotics, drawing…",
    searchLabel: "Subject search",
    categories: "Categories",
    noHits: "Can't find it? Write the subject in the comment — we'll add it.",
    priceTutor: "Price, ₼",
    priceClub: "Price, ₼",
    priceUnit: "What the price is for",
    priceUnits: (club) => ({ lesson: club ? "Per class" : "Per lesson", month: "Monthly", package: "Package" }),
    priceUnitNote: "Choose the format so parents can compare prices",
    lessonsPerMonth: (club) => (club ? "Classes per month" : "Lessons per month"),
    lessonsInPackage: (club) => (club ? "Classes in the package" : "Lessons in the package"),
    lessonMinutes: (club) => (club ? "Class length, minutes" : "Lesson length, minutes"),
    other: "Other",
    lessonPriceHint: "Is this the price of one lesson? If it is a monthly fee, choose “Monthly”.",
    format: "Format",
    trialFree: "First lesson free",
    teachLangs: "Teaching language",
    teachLangsHint: "Which languages you teach in — you can pick several. We match you with parents by it.",
    grades: "Grades",
    age: "Age",
    ageFrom: "from",
    ageTo: "to",
    years: "years",
    pinTitleTutor: "Where you are on the map",
    pinTitleClub: "Address on the map",
    district: "District",
    travelDistricts: "Districts you travel to",
    alsoTravel: "I can also come to the student",
    alsoTravelsTo: "Also comes to the student",
    address: "Address",
    addressPh: "Address: street and building number",
    mapHint: "Tap your building — the pin will go on it",
    mapLoading: "Loading the map…",
    mapNoKey: "The map is unavailable right now — enter the address as text, we'll place the pin when we review your profile.",
    searchPh: "Address or metro",
    mapZoomed: "Zoomed in — move the map so the pin is on your building, then choose the place",
    metroAbbr: "metro",
    pinConfirm: "correct?",
    landmark: "Landmark",
    landmarkPh: "For example: opposite School No. 6, entrance from the yard",
    optional: "· optional",
    subjectOther: "Other",
    subjectOtherPh: "Which subject? For example: chess for adults",
    pinPlaceLine: (d, m) => (m ? `District: ${d} · Metro: ${m}` : `District: ${d}`),
    districtFix: "Change",
    pinChange: "Remove pin",
    metroLabel: "Metro",
    metroNone: "No metro nearby",
    pinMetroWarn: (m) => `The pin is near ${m} metro — is that right?`,
    city: "City / region",
    cityPick: "Choose",
    cityFind: "Start typing: Khirdalan, Sumgait…",
    cityNoHits: "Nothing found — try another spelling",
    mapOpen: "Choose on the map",
    mapReopen: "Change on the map",
    mapChoose: "Choose this place",
    mapMe: "My location",
    mapMeFail: "We couldn't find your location — move the map yourself",
    mapBack: "Back",
    mapDragHint: "Drag the map with one finger — the pin stays in the centre",
    travelWholeCity: (city) => `Anywhere in ${city}`,
    phone: "Phone (WhatsApp) *",
    instagram: "Your Instagram (optional)",
    igCheck: "is this your profile?",
    emailNote: "Your profile edit link and the review result will come to this address",
    email: "Email *",
    about: "Comment",
    consent: ["I agree to the ", "Terms for partners", ""],
    send: "Send application",
    sending: "Sending…",
    sent: { title: "Profile submitted", text: "We'll review your profile and email [email] before it goes live.", button: "Back to home" },
    errGeneric: "Couldn't send the application. Please try again or write to support@yolutap.com.",
    districtPick: "Choose a district",
    errCheck: "Not sent yet: please check the fields marked in red.",
    errs: {
      nameTutor: "Enter your name — at least two letters.",
      nameClub: "Enter the name.",
      subjectsTutor: "Choose at least one subject.",
      subjectsClub: "Choose at least one activity.",
      price: "The price must be a number.",
      age: "The “from” age can't be greater than the “to” age.",
      pin: "Place a pin on your building.",
      address: "The map didn't load — enter the address as text.",
      district: "Choose a district.",
      travel: "Choose at least one district you travel to.",
      phone: "Enter a phone number: digits, +, spaces, brackets or dashes.",
      email: "Check your email address — it has a typo.",
      emailEmpty: "Enter your email — the review result will come there.",
      instagram: "Username only, e.g. gulay_muellim",
      mode: "Choose: my building, I travel to students, or online only",
      city: "Choose a city or region.",
      pinOutside: "The pin is outside Azerbaijan — find your building on the map.",
      consent: "Please confirm that you agree to the terms.",
      priceUnit: "Choose what the price is for: a lesson, a month or a package.",
      lessons: "Enter how many lessons the package has.",
    },
    seenAs: "How parents will see you",
    pvLead: "Check the card before sending. Spotted a mistake? Tap «Edit» next to the block.",
    pvFix: "Edit",
    pvSend: "Looks good — send",
    pvBack: "Back to the form",
    pvNone: "not set",
    pvName: "Name and photo",
    pvSubjects: "Subjects",
    pvPrice: "Price",
    pvPlace: "Where lessons happen",
    pvDetails: "Format and grades",
    pvContacts: "Contacts",
    chooseSubjects: "Choose subjects",
    chooseDirections: "Choose activities",
    perLesson: "per lesson",
    perMonth: "per month",
    pinOnMap: "Pin on the map",
    pinText: { club: "exactly on the organisation's building", building: "on your building, without flat or floor", travel: "a travel zone instead of a pin", online: "no pin, in the “Online” list" },
    nearMetro: "near metro",
    termsTitle: "Terms in short",
    terms: [
      ["Free.", "Listing on the map costs nothing: no subscription and no commission on students."],
      ["You control your address.", "You choose what parents see: the exact building or the area you travel to."],
      ["Only true information.", "List your real subjects, prices and experience."],
      ["We review every profile", "before it goes live and may ask a clarifying question."],
      ["The map doesn't affect reports.", "Recommendations in Yolutap career reports don't depend on who is listed on the map."],
      ["Edit or remove your profile", "at any time: write to support@yolutap.com."],
      ["If the terms ever change,", "we'll write to every partner in advance. Whether to stay is your call."],
    ],
    faqTitle: "Questions",
    faq: [
      { q: "How much does it cost?", a: "Sign-up, the profile and a place on the map are free, with no commission on students. If the terms ever change, we'll write to every partner in advance." },
      { q: "When will I appear on the map?", a: "We're gathering the first partners now. We'll review your profile and write to you before it goes live." },
      { q: "Does the map affect a child's report?", a: "No. The report's recommendations never depend on who is listed on the map. Tutors and clubs are shown in a separate block labelled “Yolutap partners near you”." },
      { q: "I teach online. Is this for me?", a: "Yes. Online tutors are shown in a separate list without a pin on the map." },
    ],
  },
};
